import { z } from "zod";
/** Single source of truth for released BFF page-type names. */
export const RELEASED_PAGE_NAMES = [
    "directory",
    "lrp",
    "networks",
    "network-layout",
    "network-members",
    "network-about",
    "network-leadership",
    "network-contact",
    "editors-notes",
    "market-overview",
    "practice-areas-list",
    "region",
    "about",
    "contact",
    "firm-lawyers",
    "lawyer-profile",
    "firm-layout",
    "teams",
    "team-detail",
    "diversity",
    "firm-rankings",
    "practice-area-rankings",
    "practice-area-lawyers",
    "practice-area-focus-on",
    "practice-group-overviews",
    "firm-profile-comparative-guides",
    "juniors",
    "silks",
    "arbitrators",
    "doing-business-in",
    "doing-business-in-article",
    "focus-on",
    "focus-on-article",
    "news-and-developments",
    "news-and-developments-article",
    "interview-with",
    "interview-with-article",
    "jurisdictions",
    "client-testimonials",
    "video",
    "firm-report-card",
    "lawyer-report-card",
    "summary-commentary",
    "disputes-services-layout",
    "disputes-services-practice-areas",
    "disputes-services-interviews",
    "cms-pages",
    "policy-versions",
];
export const FAMILIES = ["core", "static", "amplify", "intelligence"];
export const FamilySchema = z.enum(FAMILIES);
export const SlotSchema = z.string().regex(/^[a-z0-9-]+$/);
export const KeyspaceSchema = SlotSchema;
export const SyncPhaseSchema = z.enum([
    // Pre-sync phases derived by platform-api from the k8s pod, before the
    // sync subprocess has started writing its own phase to BFF Redis.
    "scheduling",
    "pulling-image",
    "snapshot-init-download",
    // Sync subprocess phases (written by services/legal500-bff/src/sync.ts).
    "preparing",
    "downloading-snapshot",
    "loading-snapshot",
    "building-page-list",
    "connecting-redis",
    "cloning-from-slot",
    "flushing-target",
    "creating-indexes",
    "syncing-pages",
    "writing-sitemap-sets",
    "writing-metadata",
]);
export const SYNC_PHASE_LABELS = {
    scheduling: "Waiting for a node",
    "pulling-image": "Pulling sync image",
    "snapshot-init-download": "Downloading snapshot to pod",
    preparing: "Preparing",
    "downloading-snapshot": "Downloading SQLite snapshot",
    "loading-snapshot": "Loading snapshot",
    "building-page-list": "Building page list",
    "connecting-redis": "Connecting to Redis",
    "cloning-from-slot": "Cloning from source slot",
    "flushing-target": "Flushing target slot",
    "creating-indexes": "Creating search indexes",
    "syncing-pages": "Syncing pages",
    "writing-sitemap-sets": "Writing sitemaps",
    "writing-metadata": "Writing metadata",
};
export const ReleasePublicationOverrideSchema = z.object({
    publicationId: z.number(),
    postStatuses: z.array(z.enum(["publish", "draft", "pending", "trash", "new"])).min(1),
});
export const ReleaseDefinitionSchema = z.object({
    publications: z.array(ReleasePublicationOverrideSchema),
    demotionPolicy: z.enum(["one", "none"]),
    asAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const PRODUCTION_RELEASE_DEFINITION = {
    publications: [],
    demotionPolicy: "none",
};
// Intelligence key layout: v1 hash-tags every key, v2 leaves `doc:` keys untagged. Absent means v1.
export const KeyspaceLayoutSchema = z.enum(["v1", "v2"]);
export const SlotMetadataSchema = z.object({
    keyspace: SlotSchema,
    family: FamilySchema,
    runId: z.string().nullable().optional(),
    phase: SyncPhaseSchema.nullable().optional(),
    status: z.enum(["running", "completed", "failed", "empty"]),
    startedAt: z.number(),
    finishedAt: z.number().nullable(),
    triggeredBy: z.string(),
    source: z.object({
        snapshotS3Path: z.string().optional(),
        snapshotChecksum: z.string().optional(),
        syncCommit: z.string().optional(),
        seededFromKeyspace: SlotSchema.optional(),
    }),
    pageTypes: z.array(z.object({
        name: z.string(),
        expectedCount: z.number(),
        succeededCount: z.number(),
        failedCount: z.number(),
        durationMs: z.number(),
    })),
    failures: z
        .array(z.object({
        pageType: z.string(),
        path: z.string(),
        locale: z.string(),
        error: z.string(),
        attempts: z.number(),
    }))
        .max(200),
    totals: z.object({
        docKeyCount: z.number(),
        redisKeyCount: z.number().optional().default(0),
        durationMs: z.number(),
    }),
    releaseDefinition: ReleaseDefinitionSchema.optional(),
    projectionVersion: z.string().optional(),
    layout: KeyspaceLayoutSchema.optional(),
});
export const SyncRunMetadataSchema = SlotMetadataSchema;
export const ActiveSlotRouteSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("legacy"),
    }),
    z.object({
        mode: z.literal("slot"),
        slot: SlotSchema,
        sinceMs: z.number(),
        swappedBy: z.string(),
        layout: KeyspaceLayoutSchema.optional(),
    }),
]);
export const ActiveKeyspacePointerSchema = ActiveSlotRouteSchema;
export const SlotDirectoryEntrySchema = SlotMetadataSchema;
export const SlotDirectorySchema = z.array(SlotDirectoryEntrySchema);
export const KeyspaceDirectoryEntrySchema = SlotDirectoryEntrySchema;
export const KeyspaceDirectorySchema = SlotDirectorySchema;
export const PreviewLinkPayloadSchema = z.object({
    label: z.string(),
    expiresAt: z.number(),
});
export const SyncJobArgsSchema = z.object({
    keyspace: SlotSchema,
    family: FamilySchema,
    clearAllData: z.boolean(),
    pageTypes: z.array(z.string()).nullable(),
    snapshotS3Path: z.string(),
    triggeredBy: z.string(),
    seedFromKeyspace: SlotSchema.nullable().optional(),
    releaseDefinition: ReleaseDefinitionSchema.optional(),
});
export const SlotProgressSchema = z.object({
    keyspace: SlotSchema,
    family: FamilySchema,
    runId: z.string().nullable(),
    status: z.enum(["running", "completed", "failed"]).nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    totalPages: z.number(),
    processedCount: z.number(),
    failedCount: z.number(),
    workersActive: z.number(),
    pagesPerSecond: z.number(),
    durationSeconds: z.number().nullable(),
});
export const KeyspaceProgressSchema = SlotProgressSchema;
export const SyncJobStatusSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("queued") }),
    z.object({ state: z.literal("running"), startedAt: z.number() }),
    z.object({ state: z.literal("succeeded"), durationMs: z.number() }),
    z.object({ state: z.literal("failed"), error: z.string() }),
]);
//# sourceMappingURL=index.js.map